import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { TransactionReportingService } from 'app/core/services/transaction-reporting.service';
import { Subscription, Observable, forkJoin, of } from 'rxjs';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { isNullOrUndefined } from 'util';
import { WalletByTransaction } from 'app/core/models/walletByTransaction';
import { PosService } from 'app/core/services/pos.service';
import { mergeMap, map } from 'rxjs/operators';
import { MerchantService } from 'app/core/services/merchant.service';
import { Merchant } from 'app/core/models/merchant';

@Component({
  selector: 'app-supplier-transaction-reports',
  templateUrl: './supplier-transaction-reports.component.html',
  styleUrls: ['./supplier-transaction-reports.component.css']
})
export class SupplierTransactionReportsComponent implements OnInit {

  transactionReportForm: FormGroup;
  transactionReportTypeItems: Array<String> = Array<String>("TRANSACTIONS_PER_POS", "TRANSACTIONS_PER_MERCHANT", "TRANSACTIONS_PER_MERCHANT_PER_POS");
  transactionReportTypeSub: Subscription;
  transactionReportPeriodSub: Subscription;
  numberOfItemsSubscription: Subscription;
  merchantsItemsSubscription: Subscription;
  subscription: Subscription;
  chartTypeSubscription: Subscription;
  isLoading: boolean;

  numberOfItems: number = 10;
  period: number = 3;
  chartType: String = "bar";
  periodItems: Array<number> = Array<number>(1, 3, 6, 12);
  numberOfItemsList: Array<number> = Array<number>(3, 5, 10, 20);
  merchants : Array<any> = new Array<any>();
  transactionReportType: string = "TRANSACTIONS_PER_MERCHANT";
  transactionsReport: Array<WalletByTransaction> = new Array<WalletByTransaction>();
  displayedData: Array<WalletByTransaction> = new Array<WalletByTransaction>();
  selectedMerchant;
  newChartData: number[];
  reportData: number[] = [];
  reportLabel: string[] = [];
  reportTitle: string;
  fromDate: Date;
  isPerMerchant: boolean;
  isPerMerchantAndPos: boolean;

  criteria = {
    count: this.numberOfItems,
    windowBegin: this.getFromDate(3),
    windowEnd: new Date()
  };

  constructor(
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private tsxReportingService: TransactionReportingService,
    private notifService: NotifMessageService,
    private merchantService: MerchantService,
    private posService: PosService) { }

  ngOnInit() {
    this.transactionReportForm = this.formBuilder.group({
      transactionReportType: new FormControl(this.transactionReportType, Validators.required),
      period: new FormControl(this.period, Validators.required),
      numberOfItems: new FormControl(this.numberOfItems, Validators.required),
      merchantsItems: new FormControl(),
      chartType: new FormControl('bar')
    });

    this.reportTitle = this.translate.instant("TRANSACTIONS_PER_MERCHANT");

    this.getNumberOfTxByMerchant();

    this.numberOfItemsSubscription = this.transactionReportForm.get('numberOfItems').valueChanges.subscribe(val => {
      this.numberOfItems = val;
      if (this.isFormValid()) {
        this.criteria.count = this.numberOfItems;
        if (this.transactionReportType == "TRANSACTIONS_PER_MERCHANT") {
          this.getNumberOfTxByMerchant();
        } else if (this.transactionReportType == "TRANSACTIONS_PER_POS") {
          this.getNumberOfTxByPos();
        } else if (this.transactionReportType == "TRANSACTIONS_PER_MERCHANT_PER_POS") {
          this.getTxCountByMerchantAndByPoS();
        }
      }
    });

    this.merchantsItemsSubscription = this.transactionReportForm.get('merchantsItems').valueChanges.subscribe(val => {
      if (val != null && this.selectedMerchant != val) {
        this.selectedMerchant = val;
        this.filterBySelectedMerchant();
      }
    });

    this.transactionReportTypeSub = this.transactionReportForm.get("transactionReportType").valueChanges.subscribe(val => {
      this.transactionReportType = val;
      if (this.isFormValid()) {
        this.criteria.windowEnd = new Date();
        this.criteria.windowBegin = this.getFromDate(this.period);
        if (val == "TRANSACTIONS_PER_MERCHANT") {
          this.getNumberOfTxByMerchant();
        } else if (val == "TRANSACTIONS_PER_POS") {
          this.getNumberOfTxByPos();
        } else if (val == "TRANSACTIONS_PER_MERCHANT_PER_POS") {
          this.getTxCountByMerchantAndByPoS();
        }
      }
    });

    this.transactionReportPeriodSub = this.transactionReportForm.get("period").valueChanges.subscribe(val => {
      this.period = val;
      if (this.isFormValid()) {
        this.criteria.windowEnd = new Date();
        this.criteria.windowBegin = this.getFromDate(val);
        if (isNullOrUndefined(this.transactionReportType)) {
          this.getNumberOfTxByMerchant();
        }
        if (this.transactionReportType == "TRANSACTIONS_PER_MERCHANT") {
          this.getNumberOfTxByMerchant();
        } else if (this.transactionReportType == "TRANSACTIONS_PER_POS") {
          this.getNumberOfTxByPos();
        } else if (this.transactionReportType == "TRANSACTIONS_PER_MERCHANT_PER_POS") {
          this.getTxCountByMerchantAndByPoS();
        }
      }
    });

    this.chartTypeSubscription = this.transactionReportForm.get("chartType").valueChanges.subscribe(val => {
      this.chartType = val;
    });

  }

  private filterBySelectedMerchant() {
    this.transactionsReport = this.displayedData.filter(items => items.merchantId == this.selectedMerchant);
    this.reportData.length = 0;
    this.newChartData = this.transactionsReport.map(items => items.transactionCount);
    this.reportData.push(...this.newChartData);
    this.reportLabel = this.transactionsReport.map(items => items.posName);
  }

  getFromDate(period: number): Date {
    this.fromDate = new Date();
    this.fromDate.setMonth(this.fromDate.getMonth() - period);
    return this.fromDate;
  }

  getNumberOfTxByMerchant(): void {
    this.isPerMerchant = true;
    this.isPerMerchantAndPos = false;
    this.isLoading = true;
    this.tsxReportingService.getTxCountByMerchant(this.criteria).subscribe(
      items => {
        if (items.length == 0) {
          this.notifService.notifyInfo('EMPTY_RESULT');
        }
        else {
          this.transactionsReport = items;
          this.reportLabel = [];
          this.reportData = [];
          this.merchantService.getMerchantsByIds(items.map(items => items.merchantId)).subscribe(
            merchantsList => {
              const map: Map<string, string> = new Map(merchantsList.map(obj => [obj.id.toString(), obj.name]));
              this.transactionsReport.forEach((row, index) => {
                row.merchantName = map.get(row.merchantId);
                this.reportLabel[index] = row.merchantName;
              });
              this.reportTitle = this.translate.instant("TRANSACTIONS_PER_MERCHANT");
            }
          );
          this.reportData = items.map(items => items.transactionCount);
        }
        this.isLoading = false;
      });
  }
  isFormValid(): boolean {
    if (this.transactionReportForm.get("transactionReportType").valid && this.transactionReportForm.get("period").valid
      && this.transactionReportForm.get("numberOfItems").valid)
      return true;
    return false;
  }
  getNumberOfTxByPos(): void {
    this.isPerMerchant = false;
    this.isPerMerchantAndPos = false;
    this.isLoading = true;
    this.reportTitle = this.translate.instant("TRANSACTIONS_PER_POS");
    let transactionsReportData: Array<WalletByTransaction> = new Array<WalletByTransaction>();
    this.getMergedData().subscribe(data => {
      data.sort((a, b) => a.posId.localeCompare(b.posId));
      data.forEach((mergedData, index) => {
        if (mergedData) {
          if (index > 0 && data[index].posId === data[index - 1].posId) {
            mergedData.transactionCount = data[index].transactionCount + data[index - 1].transactionCount;
            transactionsReportData.pop();
            transactionsReportData.push(mergedData);
          } else {
            transactionsReportData.push(mergedData);
          }
        }
      })
      this.transactionsReport = transactionsReportData;
      this.reportData = this.transactionsReport.map(items => items.transactionCount);
      this.reportLabel = this.transactionsReport.map(items => items.posName);
      this.isLoading = false;
    })
  }

  getTxCountByMerchantAndByPoS() {
    this.isPerMerchant = false;
    this.isPerMerchantAndPos = true;
    this.isLoading = true;
    this.reportTitle = this.translate.instant("TRANSACTIONS_PER_MERCHANT_PER_POS");

    this.getTxCountByMerchantAndByPoSObservable().subscribe(data => {
      this.displayedData = data;
      const selectedMerchantStillExists = this.selectedMerchant != null && this.merchants.map(element => element.id).includes(this.selectedMerchant)
      const merchantIdFilteParam = selectedMerchantStillExists ? this.selectedMerchant : this.merchants[0].id;
      if (this.selectedMerchant != null && !selectedMerchantStillExists) {
        this.selectedMerchant = merchantIdFilteParam;
      }
      this.transactionsReport = this.displayedData.filter(items => items.merchantId == merchantIdFilteParam);
      this.reportData = this.transactionsReport.map(items => items.transactionCount);
      this.reportLabel = this.transactionsReport.map(items => items.posName);
      this.isLoading = false;
    })


  }

  public getMergedData(): Observable<any> {
    let mergedData: Array<WalletByTransaction> = new Array<WalletByTransaction>();
    return this.tsxReportingService.getNumberOfTxByPos(this.criteria)
      .pipe(
        mergeMap((result: Array<WalletByTransaction>) => {
          if (!(result.length == 0)) {
            let posList = result.map(item => this.posService.getPosByIdForReport(item.posId));
            return forkJoin(...posList).pipe(
              map((numberOfTxByPosList) => {
                result.forEach((walletByTransaction, index) => {
                  if (numberOfTxByPosList[index]) {
                    walletByTransaction.posName = numberOfTxByPosList[index].name;
                    mergedData.push(walletByTransaction);
                  }
                })
                return mergedData;
              })
            )
          }
          else{
            this.notifService.notifyInfo('EMPTY_RESULT');
            this.isLoading = false;
            return of(null);
          }

        })
      );
  }

  public getTxCountByMerchantAndByPoSObservable(): Observable<any> {
    return this.tsxReportingService.getTxCountByMerchantAndPoS(this.criteria)
      .pipe(
        mergeMap((result: Array<WalletByTransaction>) => {
          let merchantIds = result.map(item => item.merchantId);
          if (!(merchantIds.length == 0)) {
            return forkJoin(this.merchantService.getMerchantsByIds(merchantIds)).pipe(
              map((merchantList) => {
                this.merchants = [].concat.apply([], merchantList.map(item => item));
                result.forEach((walletByTransaction) => {
                  walletByTransaction.merchantName = this.merchants.find(merchant => merchant.id == walletByTransaction.merchantId).name;
                });
                return result;
              })
            )
          }
          else {
            this.notifService.notifyInfo('EMPTY_RESULT');
            this.isLoading = false;
            return of(null);
          }

        }),
        mergeMap((result: Array<WalletByTransaction>) => {
          let posList = result.map(item => this.posService.getPosByIdForReport(item.posId));
          return forkJoin(...posList).pipe(
            map((posList) => {
              result.forEach((walletByTransaction, index) => {
                if (posList[index]) {
                  walletByTransaction.posName = posList[index].name;
                } else
                  walletByTransaction.posName = walletByTransaction.posId;
              });
              return result;
            }))
        })
      );
  }

  date() {
    let frDateFormat = "dd MMM y";
    let usDateFormat = "MMM dd y";
    if (this.translate.currentLang == "fr_FR")
      return frDateFormat;
    else
      return usDateFormat;
  }

  locale() {
    if (this.translate.currentLang == "fr_FR")
      return "fr_FR";
    else
      return "en_US";
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.transactionReportTypeSub) {
      this.transactionReportTypeSub.unsubscribe();
    }
    if (this.transactionReportTypeSub) {
      this.transactionReportTypeSub.unsubscribe();
    }
    if (this.numberOfItemsSubscription) {
      this.numberOfItemsSubscription.unsubscribe();
    }
    if (this.chartTypeSubscription) {
      this.chartTypeSubscription.unsubscribe();
    }
  }


}
